import { createModel } from '@rematch/core';
import { RootModel } from '../../models';
import { Api, ApiConfig } from '@shared/api';

import { ApiFolderInterface, TableItemType, DocumentModelState, ApiDocPermissionsInterface } from './interface';
import { UploadFileInterface } from './upload-modal';
import './i18n';
import { I18n } from '@pc/components';

const DocumentModel = createModel<RootModel>()({
  name: 'DocumentModel',
  state: {
    id: '',
    name: '',
    path: [],
    files: [],
    subFolders: [],
    tableData: [],
    modifyPerissions: [],
  } as any as DocumentModelState,
  reducers: {
    setState(state, payload: DocumentModelState): DocumentModelState {
      return {
        ...state,
        ...payload,
      };
    },
    setTableData(state, tableData: TableItemType[]): DocumentModelState {
      return {
        ...state,
        tableData,
      };
    },
    setModifyPerissions(state, modifyPerissions: UploadFileInterface[]) {
      return {
        ...state,
        modifyPerissions,
      };
    },
  },
  effects: (dispatch) => ({
    // 获取文件夹
    async getDocFolder(payload: { folderId: string; sorter: string }) {
      const { DocumentModel } = dispatch;
      const newFolderId = payload.folderId || '';
      const result = (await Api.request(ApiConfig.getDocFolder, {
        id: newFolderId,
        sort: payload.sorter,
      })) as ApiFolderInterface;
      const { id, name, path, sub_folders: subFolders, files } = result;
      // 路径组合
      let currentPath: DocumentModelState['path'] = [{ id, name, created_at: '', updated_at: '' }];
      if (Array.isArray(path)) currentPath = [...path, ...currentPath];
      // 文件夹和文件的组合
      let tableData: DocumentModelState['tableData'] = [];
      const parentPath = currentPath.map((item) => item.name).join('/');
      if (Array.isArray(subFolders)) {
        const tableDataSubfolders = subFolders.map((item) => ({
          ...item,
          type: 'folder',
          path: `${parentPath}/${item.name}`,
          creator: item.creator_name_en,
        }));
        tableData = [...tableData, ...tableDataSubfolders];
      }
      if (Array.isArray(files)) {
        const tableDataFiles = files.map((item) => ({
          ...item,
          id: item.id,
          name: item.file_name,
          updated_at: item.uploaded_at,
          created_at: item.uploaded_at,
          type: 'file',
          path: `${parentPath}/${item.file_name}`,
          creator: item.uploader_name_en,
        }));
        tableData = [...tableData, ...tableDataFiles];
      }

      DocumentModel.setState({
        id,
        name,
        tableData,
        path: currentPath,
        subFolders: subFolders || [],
        files: files || [],
      });
    },
    async search(key: string) {
      const { DocumentModel } = dispatch;
      const id = '';
      const name = '';
      const { files, folders } = await Api.request(ApiConfig.searchDocFiles, { key });
      const currentPath: DocumentModelState['path'] = [{ id, name, created_at: '', updated_at: '' }];
      const parentPath = currentPath.map((item) => item.name).join('/');
      let tableData: DocumentModelState['tableData'] = [];

      if (Array.isArray(folders)) {
        const tableDataSubfolders = folders.map((item) => ({
          ...item,
          type: 'folder',
          path: `${parentPath}/${item.name}`,
          creator: item.creator_name_en,
        }));
        tableData = [...tableData, ...tableDataSubfolders];
      }
      if (Array.isArray(files)) {
        const tableDataFiles = files.map((item) => ({
          ...item,
          id: item.id,
          name: item.file_name,
          updated_at: item.uploaded_at,
          created_at: item.uploaded_at,
          type: 'file',
          path: `${parentPath}/${item.file_name}`,
          creator: item.uploader_name_en,
        }));
        tableData = [...tableData, ...tableDataFiles];
      }
      DocumentModel.setState({
        id,
        name,
        tableData,
        path: currentPath,
        subFolders: folders || [],
        files: files || [],
      });
    },
    // 创建文件夹
    async createFolder(payload: { parent_id: string; folder_name: string }): Promise<ApiFolderInterface> {
      const { folder_name: folderName } = payload;
      const err = dispatch.DocumentModel.checkFolderName(folderName);
      if (err) return Promise.reject(err);
      return await Api.request(ApiConfig.postDocFolder, payload);
    },
    // 更新文件夹名称
    async updateFolderName(payload: { id: string; folder_name: string }) {
      await Api.request(ApiConfig.putDocFolder, { ...payload });
    },
    async deleteFile(id: string, rootState) {
      await Api.request(ApiConfig.deleteDocFile, { id });
      const { tableData } = rootState.DocumentModel;
      const newTableData = tableData.filter((item) => item.id !== id);
      dispatch.DocumentModel.setTableData(newTableData);
    },
    async deleteFolder(id: string, rootState) {
      await Api.request(ApiConfig.deleteDocFolder, { id });
      const { tableData } = rootState.DocumentModel;
      const newTableData = tableData.filter((item) => item.id !== id);
      dispatch.DocumentModel.setTableData(newTableData);
    },
    // 检查文件夹名称是否重复
    checkFolderName(folderName: string, rootState) {
      const { subFolders } = rootState.DocumentModel;
      const folder = subFolders.find((item) => item.name === folderName);
      if (folder) return I18n.t('document:folder_exists', { folderName });
      return false;
    },
    async getFileOrFolderPermissions(payload: { file_ids?: string; folder_ids?: string }) {
      const result = (await Api.request(ApiConfig.getDocPermission, { ...payload })) as ApiDocPermissionsInterface[];
      if (!result || !result.length) return Promise.reject(I18n.t('document:no_file'));
      dispatch.DocumentModel.setModifyPerissions(
        result.map((item) => ({ ...item, name: item.file_name, file_path: `${item.file_path}/${item.file_name}` })),
      );
      await dispatch.SetPermissionsModel.initData('');
    },
    async getFilePassword(id: string) {
      const result = await Api.request(ApiConfig.postDocFilesPassword, { id });
      return result as { file_id: string; password: string };
    },
  }),
});

export default DocumentModel;
